import React, { useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker.js";
import moment from "moment";
import {
  durationDaysToMonths,
  formatAgreementMonths,
} from "../../utils/packagePricingUtils.js";
import "./companySubscriptions.scss";

const MODAL_Z_SUBSCRIPTION = 10450;
const POPPER_Z_SUBSCRIPTION = 10650;

const field = (obj, key) => obj?.[key] ?? obj?.[key.charAt(0).toUpperCase() + key.slice(1)];

const getPriceAgreementMonths = (priceRow) => {
  const months = parseInt(field(priceRow, "agreementMonths"), 10);
  if (months > 0) return months;
  return durationDaysToMonths(field(priceRow, "duration"));
};

const CompanySubscriptions = ({ CompID, currency = "USD" }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningRenewalJob, setRunningRenewalJob] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  const [addons, setAddons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    packageID: "",
    packagePriceID: "",
    startDate: moment().format("YYYY-MM-DD"),
    subscriptionPrice: "",
    isAutoRenewal: true,
  });

  const fetchSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);

      const subResponse = await axiosInstance.get(APIs.GET_COMPANY_ACTIVE_SUBSCRIPTION, {
        params: { compID: CompID },
      });

      if (subResponse.data.status && subResponse.data.data) {
        const subData = subResponse.data.data;
        setSubscription(subData);

        const pkgId = field(subData, "packageID");
        if (pkgId) {
          const pkgResponse = await axiosInstance.get(APIs.GET_PACKAGE_WITH_PRICES, {
            params: { packageID: pkgId },
          });
          if (pkgResponse.data.status) {
            setPackageDetails(pkgResponse.data.data);
          }
        } else {
          setPackageDetails(null);
        }

        const subId = field(subData, "subscriptionID");
        if (subId) {
          const addonsResponse = await axiosInstance.get(APIs.GET_COMPANY_ACTIVE_ADDONS, {
            params: { subscriptionID: subId },
          });
          if (addonsResponse.data.status && Array.isArray(addonsResponse.data.data)) {
            setAddons(addonsResponse.data.data);
          } else {
            setAddons([]);
          }
        } else {
          setAddons([]);
        }
      } else {
        setSubscription(null);
        setPackageDetails(null);
        setAddons([]);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
      showErrorToast("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_PACKAGES_WITH_PRICES);
      if (response.data.status && Array.isArray(response.data.data)) {
        const active = response.data.data.filter((p) => field(p, "pStatus") === true);
        setPackages(active);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      showErrorToast("Failed to load packages");
    }
  }, []);

  useEffect(() => {
    if (CompID) {
      fetchSubscriptionData();
    }
  }, [CompID, fetchSubscriptionData]);

  const selectedPackage = useMemo(
    () => packages.find((p) => String(field(p, "id")) === String(addForm.packageID)),
    [packages, addForm.packageID]
  );

  const packagePrices = useMemo(() => {
    const prices = selectedPackage?.prices ?? selectedPackage?.Prices ?? [];
    if (!Array.isArray(prices)) return [];
    const cur = (currency || "").trim().toLowerCase();
    const matched = prices.filter(
      (p) => (field(p, "priceCurrency") || "").trim().toLowerCase() === cur
    );
    return matched.length > 0 ? matched : prices;
  }, [selectedPackage, currency]);

  const selectedPrice = useMemo(
    () =>
      packagePrices.find(
        (p) => String(field(p, "packagePriceID")) === String(addForm.packagePriceID)
      ),
    [packagePrices, addForm.packagePriceID]
  );

  const openAddModal = async () => {
    await fetchPackages();
    setAddForm({
      packageID: "",
      packagePriceID: "",
      startDate: moment().format("YYYY-MM-DD"),
      subscriptionPrice: "",
      isAutoRenewal: true,
    });
    setShowAddModal(true);
  };

  const applyPriceSelection = (priceRow) => {
    if (!priceRow) return;
    const price = parseFloat(field(priceRow, "price") || field(priceRow, "actualPrice") || 0);
    setAddForm((prev) => ({
      ...prev,
      packagePriceID: String(field(priceRow, "packagePriceID")),
      subscriptionPrice: price > 0 ? String(price) : prev.subscriptionPrice,
    }));
  };

  const handlePackageChange = (packageID) => {
    const pkg = packages.find((p) => String(field(p, "id")) === String(packageID));
    const prices = pkg?.prices ?? pkg?.Prices ?? [];
    const cur = (currency || "").trim().toLowerCase();
    const list = Array.isArray(prices)
      ? prices.filter((p) => (field(p, "priceCurrency") || "").trim().toLowerCase() === cur)
      : [];
    const priceList = list.length > 0 ? list : prices;
    const firstPrice = priceList[0];

    setAddForm((prev) => ({
      ...prev,
      packageID: String(packageID),
      packagePriceID: firstPrice ? String(field(firstPrice, "packagePriceID")) : "",
      subscriptionPrice: firstPrice
        ? String(parseFloat(field(firstPrice, "price") || field(firstPrice, "actualPrice") || 0))
        : "",
    }));
  };

  const handleRunRenewalInvoiceJob = async () => {
    try {
      setRunningRenewalJob(true);
      const response = await axiosInstance.post(APIs.POST_RUN_RENEWAL_INVOICE_JOB);
      if (response.data.status) {
        const created = response.data.data?.createdCount ?? 0;
        const leadDays = response.data.data?.leadDays ?? 7;
        showSuccessToast(
          created > 0
            ? `Created ${created} renewal invoice(s) for companies expiring within ${leadDays} days.`
            : `No renewal invoices were due (within ${leadDays} days).`
        );
      } else {
        showErrorToast(response.data.message || "Failed to run renewal invoice job");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to run renewal invoice job");
    } finally {
      setRunningRenewalJob(false);
    }
  };

  const handleCreateSubscription = async () => {
    const packageID = parseInt(addForm.packageID, 10);
    const packagePriceID = parseInt(addForm.packagePriceID, 10);
    const price = parseFloat(addForm.subscriptionPrice);
    if (!packageID) {
      showErrorToast("Please select a package");
      return;
    }
    if (!packagePriceID) {
      showErrorToast("Please select a price plan");
      return;
    }
    if (!price || price < 0) {
      showErrorToast("Please enter a valid subscription price");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        compID: parseInt(CompID, 10),
        packageID,
        packagePriceID,
        startDate: moment(addForm.startDate).format("YYYY-MM-DDTHH:mm:ss"),
        endDate: null,
        status: "Active",
        isTrial: false,
        trialEndDate: null,
        isAutoRenewal: addForm.isAutoRenewal,
        subscriptionPrice: price,
        currency: currency || "USD",
      };

      const response = await axiosInstance.post(APIs.POST_CREATE_SUBSCRIPTION, payload);
      if (response.data.status) {
        showSuccessToast("Subscription created successfully");
        setShowAddModal(false);
        fetchSubscriptionData();
      } else {
        showErrorToast(response.data.message || "Failed to create subscription");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to create subscription");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Active: { color: "success", text: "Active" },
      Trial: { color: "info", text: "Trial" },
      Expired: { color: "danger", text: "Expired" },
      Cancelled: { color: "secondary", text: "Cancelled" },
      Suspended: { color: "warning", text: "Suspended" },
    };
    const statusInfo = statusMap[status] || { color: "secondary", text: status };
    return <span className={`badge bg-${statusInfo.color}`}>{statusInfo.text}</span>;
  };

  const renderAddForm = () => {
    const agreementMonths = selectedPrice ? getPriceAgreementMonths(selectedPrice) : null;
    return (
      <div className="row g-3">
        <div className="col-12">
          <div className="alert alert-info mb-0 py-2">
            <small>
              Assigns the package to this company. Access is controlled by company expiry (trial or
              last payment). A renewal invoice is auto-created about 7 days before expiry.
            </small>
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label">Package</label>
          <select
            className="form-select"
            value={addForm.packageID}
            onChange={(e) => handlePackageChange(e.target.value)}
          >
            <option value="">Select package...</option>
            {packages.map((pkg) => (
              <option key={field(pkg, "id")} value={field(pkg, "id")}>
                {field(pkg, "pName")}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Price plan</label>
          <select
            className="form-select"
            value={addForm.packagePriceID}
            onChange={(e) => {
              const priceRow = packagePrices.find(
                (p) => String(field(p, "packagePriceID")) === e.target.value
              );
              setAddForm((prev) => ({ ...prev, packagePriceID: e.target.value }));
              applyPriceSelection(priceRow);
            }}
            disabled={!addForm.packageID || packagePrices.length === 0}
          >
            <option value="">Select price plan...</option>
            {packagePrices.map((p) => (
              <option key={field(p, "packagePriceID")} value={field(p, "packagePriceID")}>
                {field(p, "priceCurrency")} {parseFloat(field(p, "price") || 0).toFixed(2)} —{" "}
                {formatAgreementMonths(getPriceAgreementMonths(p))}
              </option>
            ))}
          </select>
          {addForm.packageID && packagePrices.length === 0 && (
            <small className="text-danger">No prices configured for this package.</small>
          )}
        </div>
        <div className="col-md-6">
          <FormInput
            label={`Subscription price (${currency})`}
            type="number"
            value={addForm.subscriptionPrice}
            onchange={(e) => setAddForm({ ...addForm, subscriptionPrice: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <CustomDateTimePicker
            label="Assigned date"
            value={addForm.startDate}
            onChange={(v) => setAddForm((prev) => ({ ...prev, startDate: v }))}
            mode="date"
            popperZIndex={POPPER_Z_SUBSCRIPTION}
          />
        </div>
        {agreementMonths && (
          <div className="col-md-6">
            <label className="form-label text-muted">Billing period</label>
            <div className="form-control-plaintext fw-semibold">
              {formatAgreementMonths(agreementMonths)} per renewal
            </div>
          </div>
        )}
        <div className="col-md-6 d-flex align-items-end">
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="subAutoRenewal"
              checked={addForm.isAutoRenewal}
              onChange={(e) => setAddForm({ ...addForm, isAutoRenewal: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="subAutoRenewal">
              Auto renewal (auto-invoice before expiry)
            </label>
          </div>
        </div>
      </div>
    );
  };

  const renderSubscriptionModal = (title) =>
    showAddModal &&
    createPortal(
      <CustomModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={title}
        onSubmit={handleCreateSubscription}
        submitButtonText={saving ? "Saving..." : "Create subscription"}
        submitButtonDisabled={saving}
        showButton={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        isCentered
        zIndex={MODAL_Z_SUBSCRIPTION}
      >
        {renderAddForm()}
      </CustomModal>,
      document.body
    );

  if (loading) {
    return <Preloader />;
  }

  const addSubscriptionButton = (
    <button type="button" className="btn btn-sm btn-primary" onClick={openAddModal}>
      <i className="fa fa-plus me-1" />
      {subscription ? "Assign subscription" : "Add subscription"}
    </button>
  );

  const runRenewalJobButton = (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary ms-2"
      onClick={handleRunRenewalInvoiceJob}
      disabled={runningRenewalJob}
      title="Create renewal invoices for all companies expiring within 7 days"
    >
      <i className={`fa ${runningRenewalJob ? "fa-spinner fa-spin" : "fa-file-invoice"} me-1`} />
      {runningRenewalJob ? "Running..." : "Run renewal job"}
    </button>
  );

  const panelActions = (
    <div className="d-flex align-items-center">
      {runRenewalJobButton}
      {addSubscriptionButton}
    </div>
  );

  if (!subscription) {
    return (
      <>
        <Panel>
          <PanelHeader>
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Subscription Details</span>
              {panelActions}
            </div>
          </PanelHeader>
          <PanelBody>
            <div className="alert alert-info mb-0">
              <i className="fa fa-info-circle me-2" />
              No active subscription found for this company. Click &quot;Add subscription&quot; to assign a package.
            </div>
          </PanelBody>
        </Panel>

        {renderSubscriptionModal("Add subscription")}
      </>
    );
  }

  return (
    <>
      {renderSubscriptionModal("Assign subscription")}

      <Panel>
        <PanelHeader>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>Current Subscription</span>
            {panelActions}
          </div>
        </PanelHeader>
        <PanelBody>
          <div className="row">
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      Status:
                    </td>
                    <td>{getStatusBadge(field(subscription, "status"))}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Subscription ID:</td>
                    <td>#{subscription.subscriptionID}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Assigned:</td>
                    <td>{moment(field(subscription, "startDate")).format("MMM DD, YYYY")}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Access expires:</td>
                    <td>
                      {field(subscription, "compExpireDate") ? (
                        <>
                          {moment(field(subscription, "compExpireDate")).format("MMM DD, YYYY")}
                          {field(subscription, "isExpiringSoon") && (
                            <span className="badge bg-warning ms-2">
                              Expires in {field(subscription, "daysUntilExpiry")} days
                            </span>
                          )}
                          {field(subscription, "isExpired") && (
                            <span className="badge bg-danger ms-2">Expired</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Not set</span>
                      )}
                    </td>
                  </tr>
                  {field(subscription, "agreementMonths") && (
                    <tr>
                      <td className="fw-bold">Renewal period:</td>
                      <td>{formatAgreementMonths(field(subscription, "agreementMonths"))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "40%" }}>
                      Price:
                    </td>
                    <td className="text-success fw-bold">
                      {subscription.currency} {parseFloat(subscription.subscriptionPrice).toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Auto Renewal:</td>
                    <td>
                      {subscription.isAutoRenewal ? (
                        <span className="badge bg-success">
                          <i className="fa fa-check me-1" />
                          Enabled
                        </span>
                      ) : (
                        <span className="badge bg-secondary">
                          <i className="fa fa-times me-1" />
                          Disabled
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Created Date:</td>
                    <td>{moment(subscription.createdDate).format("MMM DD, YYYY")}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Last Updated:</td>
                    <td>{moment(subscription.updatedDate).format("MMM DD, YYYY")}</td>
                  </tr>
                  {subscription.cancellationDate && (
                    <tr>
                      <td className="fw-bold">Cancellation Date:</td>
                      <td>{moment(subscription.cancellationDate).format("MMM DD, YYYY")}</td>
                    </tr>
                  )}
                  {subscription.cancellationReason && (
                    <tr>
                      <td className="fw-bold">Cancellation Reason:</td>
                      <td>{subscription.cancellationReason}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {packageDetails && (
        <Panel>
          <PanelHeader>Package Details</PanelHeader>
          <PanelBody>
            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3">{packageDetails.pName}</h5>
                <p className="text-muted">{packageDetails.pDetail}</p>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td className="fw-bold">Allowed Devices:</td>
                      <td>{packageDetails.pAllowedDevices}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Allowed Users:</td>
                      <td>{packageDetails.pAllowedUsers}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Trial Days:</td>
                      <td>{packageDetails.pTrialDays} days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </PanelBody>
        </Panel>
      )}

      {addons.length > 0 && (
        <Panel>
          <PanelHeader>Active Addons ({addons.length})</PanelHeader>
          <PanelBody>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="bg-light">
                  <tr>
                    <th>Addon Name</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Billing Cycle</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {addons.map((addon) => (
                    <tr key={addon.companyAddonID}>
                      <td>{addon.addonName || `Addon #${addon.addonID}`}</td>
                      <td>{addon.quantity}</td>
                      <td className="text-success">
                        {addon.currency} {parseFloat(addon.addonPrice).toFixed(2)}
                      </td>
                      <td>
                        <span className="badge bg-info">{addon.billingCycle}</span>
                      </td>
                      <td>{moment(addon.startDate).format("MMM DD, YYYY")}</td>
                      <td>{moment(addon.endDate).format("MMM DD, YYYY")}</td>
                      <td className="text-center">
                        {addon.isActive ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PanelBody>
        </Panel>
      )}
    </>
  );
};

export default CompanySubscriptions;
